(() => {
  if (window.customElements.get('cart-points-vouchers')) return;

  const PANEL_CONTAINER = '.smile-panel-frame-container';
  const SMILE_UI_TIMEOUT = 8000;
  const SMILE_SDK_TIMEOUT = 8000;
  const VOUCHER_CODES_KEY = 'theme:points-voucher-codes';
  const VOUCHER_NAMES_KEY = 'theme:points-voucher-names';
  let cachedFulfillments = null;
  let cachedVoucherCodes = null;
  let cachedVoucherNames = null;
  let voucherCodesKnown = false;
  let cachedFromLive = false;
  let fulfillmentsDirty = false;
  let inflightLive = null;
  let panelSeen = false;

  function usableVouchers(fulfillments) {
    const now = Date.now();

    return (Array.isArray(fulfillments) ? fulfillments : []).filter((fulfillment) => {
      const code = String(fulfillment.code || '').trim();
      const fulfillmentStatus = fulfillment.fulfillmentStatus || fulfillment.fulfillment_status;
      const usedAt = fulfillment.usedAt || fulfillment.used_at;
      const expiresAt = fulfillment.expiresAt || fulfillment.expires_at;

      return (
        code && fulfillmentStatus === 'issued' && !usedAt && (!expiresAt || Date.parse(expiresAt) > now)
      );
    });
  }

  function persistVoucherCodes(codes, names) {
    cachedVoucherCodes = (codes || []).map((code) => String(code).trim().toLowerCase()).filter(Boolean);
    voucherCodesKnown = true;

    if (names && typeof names === 'object') {
      cachedVoucherNames = names;
    }

    try {
      sessionStorage.setItem(VOUCHER_CODES_KEY, JSON.stringify(cachedVoucherCodes));
      if (names && typeof names === 'object') {
        sessionStorage.setItem(VOUCHER_NAMES_KEY, JSON.stringify(cachedVoucherNames));
      }
    } catch (error) {
      /* 隐私模式写不进也不挡券列表本身 */
    }
  }

  function getCachedVoucherNames() {
    if (cachedVoucherNames) return cachedVoucherNames;

    try {
      const parsed = JSON.parse(sessionStorage.getItem(VOUCHER_NAMES_KEY) || 'null');
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        cachedVoucherNames = parsed;
      }
    } catch (error) {
      /* 读失败就当没有名称对照 */
    }

    return cachedVoucherNames || {};
  }

  function getVoucherLabel(code) {
    const key = String(code || '').trim().toLowerCase();
    if (!key) return '';
    return getCachedVoucherNames()[key] || String(code || '').trim();
  }

  function relabelDiscountTitles(root) {
    (root || document).querySelectorAll('[data-discount-code]').forEach((el) => {
      const code = el.getAttribute('data-discount-code');
      if (!code) return;
      el.textContent = getVoucherLabel(code);
    });
  }

  function getCachedVoucherCodes() {
    if (voucherCodesKnown && cachedVoucherCodes) return cachedVoucherCodes;

    try {
      const parsed = JSON.parse(sessionStorage.getItem(VOUCHER_CODES_KEY) || 'null');
      if (Array.isArray(parsed)) {
        cachedVoucherCodes = parsed.map((code) => String(code).trim().toLowerCase()).filter(Boolean);
        voucherCodesKnown = true;
      }
    } catch (error) {
      /* 读失败就当还不知道 */
    }

    return cachedVoucherCodes || [];
  }

  function hasKnownVoucherCodes() {
    if (!voucherCodesKnown) getCachedVoucherCodes();
    return voucherCodesKnown;
  }

  window.__getPointsVoucherCodes = getCachedVoucherCodes;
  window.__hasKnownPointsVoucherCodes = hasKnownVoucherCodes;
  window.__getPointsVoucherLabel = getVoucherLabel;
  window.__relabelPointsVoucherTitles = relabelDiscountTitles;

  // Smile 的 loader 异步注入 SmileUI，早于它的点击要等就绪后才能开面板
  function waitForSmileUI(timeout) {
    if (window.SmileUI) return Promise.resolve(window.SmileUI);

    return new Promise((resolve) => {
      const deadline = Date.now() + timeout;
      const tick = () => {
        if (window.SmileUI) return resolve(window.SmileUI);
        if (Date.now() >= deadline) return resolve(null);
        setTimeout(tick, 100);
      };

      tick();
    });
  }

  // window.Smile 出现时 SDK 还没 initialize，方法一律不可用；
  // customer.current() 拿到人才说明初始化完成且客户会话已建立
  async function waitForSmileCustomer(timeout) {
    const deadline = Date.now() + timeout;

    while (Date.now() < deadline) {
      try {
        const customer = await window.Smile?.customer?.current?.();
        if (customer) return customer;
      } catch (error) {
        // 初始化途中会抛，等下一轮
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    return null;
  }

  function fetchLive() {
    if (inflightLive) return inflightLive;

    inflightLive = Promise.resolve(window.Smile.rewardFulfillments.get())
      .then((result) => (Array.isArray(result) ? result : result?.rewardFulfillments || []))
      .then((list) => {
        cachedFromLive = true;
        fulfillmentsDirty = false;
        return list;
      })
      .finally(() => {
        inflightLive = null;
      });

    return inflightLive;
  }

  // Smile 的 loader 初始化时已经预载过 rewardFulfillments，缓存优先；
  // 自己调 preload()/get() 会带着已失效的 token 去换票，拿到 401 Invalid Bearer token。
  // 兑换后或抽屉打开时缓存不可信，改打 live，失败再退回已有列表。
  async function fetchFulfillments(preferLive) {
    if (preferLive || fulfillmentsDirty) {
      try {
        return await fetchLive();
      } catch (error) {
        if (cachedFulfillments) return cachedFulfillments;
      }
    }

    if (cachedFromLive && cachedFulfillments) return cachedFulfillments;

    const cached = window.Smile.rewardFulfillments.preloaded();
    if (Array.isArray(cached)) return cached;

    try {
      await window.Smile.preload(['rewardFulfillments']);
      return window.Smile.rewardFulfillments.preloaded() || [];
    } catch (error) {
      return fetchLive();
    }
  }

  function refreshAllVouchers() {
    fulfillmentsDirty = true;
    document.querySelectorAll('cart-points-vouchers').forEach((el) => {
      if (typeof el.refresh === 'function') el.refresh();
    });
  }

  // Smile 没有暴露面板关闭事件，但面板容器开时插入、关时移除。
  // 监听挂在 document 上，header / footer / 公告栏打开的 Redeem 关掉后也能回写券列表。
  function watchSmilePanel() {
    if (watchSmilePanel.bound || typeof MutationObserver !== 'function') return;
    watchSmilePanel.bound = true;

    new MutationObserver(() => {
      if (document.querySelector(PANEL_CONTAINER)) {
        panelSeen = true;
        return;
      }

      if (!panelSeen) return;
      panelSeen = false;
      refreshAllVouchers();
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  function bindDrawerOpenRefresh() {
    if (bindDrawerOpenRefresh.bound) return;
    bindDrawerOpenRefresh.bound = true;

    document.addEventListener(
      'dialog:before-show',
      (event) => {
        if (!(event.target instanceof HTMLElement) || event.target.id !== 'cart-drawer') return;
        fulfillmentsDirty = true;
        const list = event.target.querySelector('cart-points-vouchers');
        if (list && typeof list.refresh === 'function') list.refresh();
      },
      true
    );
  }

  class CartPointsVouchers extends HTMLElement {
    get promo() {
      return this.closest('cart-promo, cart-page-promo');
    }

    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this._abort = new AbortController();

      this.querySelector('[data-points-voucher-redeem]')?.addEventListener('click', () => this.openRedeemPanel());

      // 抽屉/购物车页重渲染后立刻用上次券列表回显，避免展开区域先闪空态、积分券先当普通标签露出
      if (cachedFulfillments) this.setVouchers(cachedFulfillments);

      // smile-js-initialized 只在首次初始化时派发一次，section 重渲染出的新元素等不到，
      // 所以这里也直接起一次，由 load() 自己轮询 SDK 就绪
      document.addEventListener('smile-js-initialized', () => this.load(), { signal: this._abort.signal });
      this.load();
    }

    disconnectedCallback() {
      this._abort?.abort();
      this._bound = false;
      this._loaded = false;
    }

    async openRedeemPanel() {
      if (this._opening) return;
      this._opening = true;

      const button = this.querySelector('[data-points-voucher-redeem]');
      if (button) button.disabled = true;

      try {
        const ui = await waitForSmileUI(SMILE_UI_TIMEOUT);
        if (!ui || typeof ui.openPanel !== 'function') return;

        if (typeof ui.ready === 'function') await ui.ready();
        await ui.openPanel({ deep_link: 'points_products' });
      } catch (error) {
        // Smile 起不来时没有可降级的入口，它不认任何 URL hash
      } finally {
        this._opening = false;
        if (button) button.disabled = false;
      }
    }

    // 兑换回来要最新数据；token 过期时不再整页刷新，清状态后走一次常规 load
    async refresh() {
      try {
        this.setVouchers(await fetchLive());
        this._loaded = true;
      } catch (error) {
        this._loaded = false;
        this._loading = false;
        this.load();
      }
    }

    async load() {
      if (!this.isConnected || this._loading || this._loaded) return;
      this._loading = true;

      try {
        const customer = await waitForSmileCustomer(SMILE_SDK_TIMEOUT);
        if (!customer) {
          persistVoucherCodes([], {});
          this.promo?.syncVoucherTags();
          return;
        }

        this.setVouchers(await fetchFulfillments(fulfillmentsDirty));
        this._loaded = true;
      } catch (error) {
        console.warn('[cart-points-vouchers] 读取 Smile 积分券失败', error);
        this.voucherCodes = [];
        persistVoucherCodes([]);
        this.promo?.syncVoucherTags();
      } finally {
        this._loading = false;
      }
    }

    // JS SDK 的 fulfillment 只有 usedAt，没有 REST 那套 usage_status
    setVouchers(fulfillments) {
      cachedFulfillments = Array.isArray(fulfillments) ? fulfillments : [];
      const vouchers = usableVouchers(cachedFulfillments);
      const codes = vouchers.map((voucher) => String(voucher.code).trim());
      const names = {};

      vouchers.forEach((voucher) => {
        const code = String(voucher.code || '').trim().toLowerCase();
        const name = String(voucher.name || '').trim();
        if (code && name) names[code] = name;
      });

      persistVoucherCodes(codes, names);
      this.voucherCodes = codes;

      if (!this.isConnected) {
        document.querySelectorAll('cart-promo, cart-page-promo').forEach((promo) => {
          if (typeof promo.syncVoucherTags === 'function') promo.syncVoucherTags();
        });
        relabelDiscountTitles();
        return;
      }

      if (vouchers.length) {
        this.render(vouchers);
      } else {
        this.showEmpty();
      }

      // 券的清单只有这里知道，拿到后才能把上方标签里属于积分券的那些收掉
      this.promo?.syncVoucherTags();
      relabelDiscountTitles();
      document.querySelectorAll('cart-selection').forEach((el) => {
        if (typeof el.render === 'function') el.render();
      });
    }

    showEmpty() {
      const options = this.querySelector('[data-points-voucher-options]');
      const empty = this.querySelector('[data-points-voucher-empty]');

      if (options) {
        options.replaceChildren();
        options.hidden = true;
      }

      if (empty) empty.hidden = false;
    }

    render(vouchers) {
      const options = this.querySelector('[data-points-voucher-options]');
      const promo = this.promo;
      if (!options || !promo) return;

      const empty = this.querySelector('[data-points-voucher-empty]');
      const appliedCodes = (promo.appliedCodes || []).map((code) => code.toLowerCase());
      const prefix = this.dataset.idPrefix || 'points-voucher';
      const fragment = document.createDocumentFragment();

      vouchers.forEach((voucher, index) => {
        const code = String(voucher.code).trim();
        const label = document.createElement('label');
        const input = document.createElement('input');
        const radio = document.createElement('span');
        const selectedIcon = document.createElement('img');
        const text = document.createElement('span');

        label.className = 'cart-points-vouchers__option';
        input.className = 'cart-points-vouchers__input';
        input.type = 'radio';
        input.name = prefix;
        input.value = code;
        input.id = `${prefix}-${index}`;
        input.checked = appliedCodes.includes(code.toLowerCase());
        input.dataset.applied = input.checked ? 'true' : 'false';

        radio.className = 'cart-points-vouchers__radio';
        radio.setAttribute('aria-hidden', 'true');
        selectedIcon.className = 'cart-points-vouchers__selected-icon';
        selectedIcon.src = this.dataset.selectedIcon;
        selectedIcon.alt = '';
        selectedIcon.width = 18;
        selectedIcon.height = 18;
        radio.appendChild(selectedIcon);
        text.className = 'cart-points-vouchers__code';
        text.textContent = String(voucher.name || '').trim() || code;

        label.classList.toggle('is-selected', input.checked);
        label.append(input, radio, text);
        input.addEventListener('click', () => this.toggle(code, promo, input));
        fragment.appendChild(label);
      });

      options.replaceChildren(fragment);
      options.hidden = false;
      if (empty) empty.hidden = true;
    }

    // 券按稿是单选，再点一次已用的那张就是取消。
    // radio 在已选中时不派发 change，所以这里听 click，并用 data-applied 记住服务端状态
    toggle(code, promo, input) {
      if (input.dataset.applied === 'true') return this.remove(code, promo, input);

      return this.apply(code, promo, input);
    }

    async apply(code, promo, input) {
      if (typeof promo.applyCode !== 'function') return;

      this.querySelectorAll('input').forEach((item) => {
        item.disabled = true;
        item.closest('.cart-points-vouchers__option')?.classList.toggle('is-selected', item === input);
      });

      const applied = await promo.applyCode(code, true);
      if (!applied) {
        this.restoreSelection(promo);
        return;
      }

      // 抽屉 / 购物车页只局部换标签、不重渲染券列表；不在这里写回，券会一直禁用，再点也走不到 remove
      this.querySelectorAll('input').forEach((item) => {
        item.disabled = false;
        item.dataset.applied = item === input ? 'true' : 'false';
      });
    }

    async remove(code, promo, input) {
      if (typeof promo.onRemove !== 'function') return;

      input.checked = false;
      input.dataset.applied = 'false';
      input.closest('.cart-points-vouchers__option')?.classList.remove('is-selected');
      this.querySelectorAll('input').forEach((item) => {
        item.disabled = true;
      });

      const removed = await promo.onRemove(code);
      if (!removed) {
        this.restoreSelection(promo);
        return;
      }

      // 移除成功后购物车会整块重渲染，这里只恢复可操作性；
      // 按旧 DOM 回填会让刚取消的券闪回选中态
      this.querySelectorAll('input').forEach((item) => {
        item.disabled = false;
      });
    }

    restoreSelection(promo) {
      const appliedCodes = (promo.appliedCodes || []).map((item) => item.toLowerCase());

      this.querySelectorAll('input').forEach((item) => {
        item.disabled = false;
        item.checked = appliedCodes.includes(item.value.toLowerCase());
        item.dataset.applied = item.checked ? 'true' : 'false';
        item.closest('.cart-points-vouchers__option')?.classList.toggle('is-selected', item.checked);
      });
    }
  }

  watchSmilePanel();
  bindDrawerOpenRefresh();
  window.customElements.define('cart-points-vouchers', CartPointsVouchers);

  if (Object.keys(getCachedVoucherNames()).length) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => relabelDiscountTitles());
    } else {
      relabelDiscountTitles();
    }
  }
})();
